import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { adminAddGallery, adminAddVideo, adminAddEvent, adminUploadFile } from '@/lib/api';
import { MediaInput } from './MediaInputs';
import { inp } from './styles';

/* ─── Add form for Gallery / Videos / Events ─── */
export function AdminForm({ tab, onSuccess }) {
  const [data, setData] = useState({});
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      if (tab === 'gallery') await adminAddGallery(data);
      else if (tab === 'videos') await adminAddVideo(data);
      else await adminAddEvent(data);
      onSuccess();
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Failed');
    }
  };

  const handleFileUpload = async (file, field) => {
    setUploading(true);
    try {
      const result = await adminUploadFile(file);
      setData(prev => ({ ...prev, [field]: result.url }));
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handle} data-testid="admin-form" className="bg-white/[0.03] border border-white/5 rounded-2xl p-6 mb-6 space-y-4">
      {err && <p className="text-red-400 text-sm">{typeof err === 'string' ? err : 'Error'}</p>}

      {tab === 'gallery' && <>
        <MediaInput
          label="Image"
          accept="image/jpeg,image/png,image/webp,image/gif"
          fileType="image"
          value={data.url || ''}
          onChange={val => setData({...data, url: val})}
          onFileUpload={f => handleFileUpload(f, 'url')}
          uploading={uploading}
          testId="admin-gallery-url"
        />
        <input placeholder="Caption" required className={inp} data-testid="admin-gallery-caption" onChange={e => setData({...data, caption: e.target.value})} />
        <select className={inp + " [&>option]:bg-[#0A1628]"} data-testid="admin-gallery-category" onChange={e => setData({...data, category: e.target.value})} required>
          <option value="">Category</option>
          <option value="facility">Facility</option>
          <option value="events">Events</option>
          <option value="sports">Sports</option>
        </select>
      </>}

      {tab === 'videos' && <>
        <MediaInput
          label="Video"
          accept="video/mp4,video/webm,video/quicktime"
          fileType="video"
          value={data.url || ''}
          onChange={val => setData({...data, url: val})}
          onFileUpload={f => handleFileUpload(f, 'url')}
          uploading={uploading}
          testId="admin-video-url"
        />
        <input placeholder="Title" required className={inp} data-testid="admin-video-title" onChange={e => setData({...data, title: e.target.value})} />
        <input placeholder="Description" required className={inp} data-testid="admin-video-desc" onChange={e => setData({...data, description: e.target.value})} />
      </>}

      {tab === 'events' && <>
        <input placeholder="Event Name" required className={inp} data-testid="admin-event-name" onChange={e => setData({...data, name: e.target.value})} />
        <input placeholder="Sport" required className={inp} data-testid="admin-event-sport" onChange={e => setData({...data, sport: e.target.value})} />
        <input placeholder="Format (optional)" className={inp} data-testid="admin-event-format" onChange={e => setData({...data, format: e.target.value})} />
        <select className={inp + " [&>option]:bg-[#0A1628]"} data-testid="admin-event-status" onChange={e => setData({...data, status: e.target.value})}>
          <option value="coming_soon">Coming Soon</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
      </>}

      <button type="submit" disabled={uploading} data-testid="admin-form-submit"
        className="bg-[#F26522] hover:bg-[#D9551A] disabled:opacity-50 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all">
        {uploading ? 'Uploading...' : 'Save'}
      </button>
    </form>
  );
}

export function GalleryGrid({ items, onDelete }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map(img => (
        <div key={img.id} data-testid={`admin-gallery-item-${img.id}`} className="relative rounded-xl overflow-hidden group aspect-video">
          <img src={img.url} alt={img.caption} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <button onClick={() => onDelete(img.id)} data-testid={`delete-gallery-${img.id}`} className="bg-red-500 hover:bg-red-600 text-white p-2.5 rounded-xl transition-colors">
              <Trash2 size={16} />
            </button>
          </div>
          <div className="absolute bottom-2 left-2 right-2 bg-black/60 rounded-lg px-2 py-1">
            <div className="text-white text-xs font-medium truncate">{img.caption}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function VideoList({ items, onDelete }) {
  return (
    <div className="space-y-3">
      {items.map(vid => (
        <div key={vid.id} data-testid={`admin-video-item-${vid.id}`} className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-xl p-4">
          <div>
            <div className="text-white font-medium text-sm">{vid.title}</div>
            <div className="text-slate-400 text-xs truncate max-w-md">{vid.url}</div>
          </div>
          <button onClick={() => onDelete(vid.id)} data-testid={`delete-video-${vid.id}`} className="text-red-400 hover:text-red-300 p-2 transition-colors">
            <Trash2 size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function EventList({ items, onDelete, onStatusChange }) {
  return (
    <div className="space-y-3">
      {items.map(ev => (
        <div key={ev.id} data-testid={`admin-event-item-${ev.id}`} className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-xl p-4">
          <div>
            <div className="text-white font-medium text-sm">{ev.name}</div>
            <div className="text-slate-400 text-xs">{ev.sport} &middot; {ev.format}</div>
          </div>
          <div className="flex items-center gap-2">
            <select value={ev.status} onChange={e => onStatusChange(ev.id, e.target.value)} data-testid={`event-status-${ev.id}`}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white [&>option]:bg-[#0A1628]">
              <option value="coming_soon">Coming Soon</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
            <button onClick={() => onDelete(ev.id)} data-testid={`delete-event-${ev.id}`} className="text-red-400 hover:text-red-300 p-2 transition-colors">
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
